/**
 * NotificationContext
 *
 * Provides global access to notification count and refetch function.
 * This allows any component to trigger a notification count refresh
 * after actions that might create notifications (e.g., risk alerts).
 */

import React from 'react';
import { useLocation } from 'react-router-dom';

import * as GQL from '~/graphql/generated/client';
import { PUBLIC_ROUTES } from '~c/components/public-routes';

// List of public route prefixes where we should skip notification queries
const PUBLIC_ROUTE_PREFIXES = Object.values(PUBLIC_ROUTES).map((route) =>
  route.split(':')[0].replace(/\/$/, '')
);

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

interface NotificationContextValue {
  /**
   * Current count of unread notifications
   */
  unreadCount: number;

  /**
   * Trigger a refetch of the unread notification count
   * Call this after mutations that might create notifications
   */
  refetchUnreadCount: () => void;

  /**
   * Loading state for the notification count query
   */
  loading: boolean;
}

const NotificationContext =
  React.createContext<NotificationContextValue | null>(null);

interface NotificationProviderProps {
  children: React.ReactNode;
}

/**
 * NotificationProvider
 *
 * Wraps the app to provide notification count state globally.
 * Uses polling (30 seconds) for background updates, but also
 * exposes refetchUnreadCount for immediate updates after mutations.
 */
export function NotificationProvider({ children }: NotificationProviderProps) {
  const location = useLocation();
  const shouldSkip = isPublicRoute(location.pathname);

  const { data, loading, refetch } = GQL.useUnreadNotificationCountQuery({
    fetchPolicy: 'cache-and-network',
    pollInterval: shouldSkip ? 0 : 30000, // Only poll on authenticated pages
    skip: shouldSkip, // Skip query entirely on public pages
  });

  const unreadCount = data?.unreadNotificationCount?.count ?? 0;

  // Memoize the refetch function to prevent unnecessary re-renders
  const refetchUnreadCount = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const value = React.useMemo(
    () => ({
      unreadCount,
      refetchUnreadCount,
      loading,
    }),
    [unreadCount, refetchUnreadCount, loading]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook to access the notification context
 * Throws if used outside of NotificationProvider
 */
export function useNotificationContext(): NotificationContextValue {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotificationContext must be used within a NotificationProvider'
    );
  }
  return context;
}

/**
 * Optional hook that returns null if used outside of NotificationProvider
 * Useful for components that may or may not be wrapped in the provider
 */
export function useNotificationContextOptional(): NotificationContextValue | null {
  return React.useContext(NotificationContext);
}
